/**
 * lib/api/operator-identity — operator-pinned identity hooks (Standard S-1.1).
 *
 * Personal-identity surfaces (the nav-foot account menu, /app/profile, security,
 * privacy, and the firm/platform console sidebars) MUST show the *logged-in
 * human*, never the client a firm is administering. These hooks force
 * `useOperatorSession: true`, so api() ignores any active act-as / impersonation
 * token and travels on the operator's own Supabase session.
 *
 * Their query keys are isolated from the act-as-following `useMe()` /
 * `useTenantMe()` caches (`['operator', ...]` vs `['users'|'tenants', 'me']`),
 * so the operator identity and the active-context identity can never collide in
 * the query cache — that collision was a contributor to the act-as bleed.
 */

import { useQuery } from '@tanstack/react-query';
import { api } from './client';
import type { MeResponse } from './users';
import type { TenantsMeResponse } from './tenants';

/** The logged-in human's account — always the operator, never the act-as client. */
export function useOperatorMe() {
  return useQuery<MeResponse>({
    queryKey: ['operator', 'me'],
    queryFn: ({ signal }) =>
      api<MeResponse>('/api/v1/users/me', { signal, useOperatorSession: true }),
  });
}

/** The logged-in human's own tenant — always the operator's, never the client's. */
export function useOperatorTenantMe() {
  return useQuery<TenantsMeResponse>({
    queryKey: ['operator', 'tenant-me'],
    queryFn: ({ signal }) =>
      api<TenantsMeResponse>('/api/v1/tenants/me', { signal, useOperatorSession: true }),
  });
}
